import { BadgeStatusType } from '@/components/Badge/BadgeTypes';
import { OilColor } from '@/helpers/ratingState';

/**
 * Maps API score enum to number rating (1-5)
 * Returns null if score is not provided
 */
export const mapScoreToNumber = (
  score?: 'ONE' | 'TWO' | 'THREE' | 'FOUR' | 'FIVE',
): number | null => {
  if (!score) return null;

  const scoreMap: Record<'ONE' | 'TWO' | 'THREE' | 'FOUR' | 'FIVE', number> = {
    ONE: 1,
    TWO: 2,
    THREE: 3,
    FOUR: 4,
    FIVE: 5,
  };

  return scoreMap[score];
};

/**
 * Maps assessment status text to BadgeStatusType
 * Used for cockpit and interior assessment items
 */
export const mapAssessmentStatus = (
  value?: string,
): BadgeStatusType | undefined => {
  if (!value) return undefined;

  const lowerValue = value.toLowerCase();
  if (
    lowerValue.includes('bez uwag') ||
    lowerValue.includes('ok') ||
    lowerValue.includes('nie używana') ||
    lowerValue.includes('dobry')
  ) {
    return BadgeStatusType.Success;
  }
  if (
    lowerValue.includes('uwaga') ||
    lowerValue.includes('ślady') ||
    lowerValue.includes('używana') ||
    lowerValue.includes('występują') ||
    lowerValue.includes('zużycie')
  ) {
    return BadgeStatusType.Error;
  }
  if (
    lowerValue.includes('uszkodzenie') ||
    lowerValue.includes('pęknięcie') ||
    lowerValue.includes('inne')
  ) {
    return BadgeStatusType.Neutral;
  }

  if (
    lowerValue.includes('brak') ||
    lowerValue.includes('nie dotyczy') ||
    lowerValue.includes('nie ma')
  ) {
    return BadgeStatusType.Info;
  }

  return undefined;
};

export const mapAssessmentStatusInner = (
  value?: string,
): BadgeStatusType | undefined => {
  if (!value) return undefined;

  const lowerValue = value.toLowerCase();
  if (
    lowerValue.includes('brak')) {
    return BadgeStatusType.Success;
  }

  if (
    lowerValue.includes('występują')) {
    return BadgeStatusType.Error;
  }

}

/**
 * Maps oil leak status to BadgeStatusType
 * "Występują" (leaks present) -> Error (red)
 * "Brak" (no leaks) -> Success (green)
 */
export const mapOilLeakStatusToBadge = (
  value?: string,
): BadgeStatusType => {
  if (!value) return BadgeStatusType.Neutral;

  const lowerValue = value.toLowerCase();

  if (lowerValue.includes('brak')) {
    return BadgeStatusType.Success;
  }

  if (lowerValue.includes('występują')) {
    return BadgeStatusType.Error;
  }

  return BadgeStatusType.Neutral;
};

/**
 * Maps headlight mounting status to BadgeStatusType
 * "Naprawiane" -> Error (red)
 * "OK" -> Success (green)
 * "Inne" -> Neutral (default)
 */
export const mapHeadlightMountingStatusToBadge = (
  value?: string,
): BadgeStatusType => {
  if (!value) return BadgeStatusType.Neutral;

  const lowerValue = value.toLowerCase();

  if (lowerValue.includes('ok')) {
    return BadgeStatusType.Success;
  }

  if (lowerValue.includes('naprawiane')) {
    return BadgeStatusType.Error;
  }

  return BadgeStatusType.Neutral;
};

export const mapAssessmentStatusAsh = (
  value?: string,
): BadgeStatusType | undefined => {
  if (!value) return undefined;

  const lowerValue = value.toLowerCase();
  if (
    lowerValue.includes('nie używana')) {
    return BadgeStatusType.Success;
  }

  if (
    lowerValue.includes('używana')) {
    return BadgeStatusType.Error;
  }
  if (
    lowerValue.includes('brak')) {
    return BadgeStatusType.Info;
  }

}

/**
 * Maps chassis status text to BadgeStatusType
 * Similar to mapEquipmentStatusToBadge but treats "brak" as Success (green)
 * because in chassis context "brak" means "no issues found"
 */
export const mapChassisStatusToBadge = (status?: string): BadgeStatusType => {
  if (!status) return BadgeStatusType.Neutral;

  const lowerStatus = status.toLowerCase();

  if (lowerStatus === 'brak') {
    return BadgeStatusType.Success;
  }

  return mapEquipmentStatusToBadge(status);
};

/**
 * Maps equipment status text to BadgeStatusType
 * Used for equipment inspection items
 */
export const mapEquipmentStatusToBadge = (status?: string): BadgeStatusType => {
  if (!status) return BadgeStatusType.Neutral;

  const lowerStatus = status.toLowerCase();

  // "BRAK" oznacza brak wyposażenia - pokazujemy Info (niebieski, tekst "Brak")
  if (lowerStatus === 'brak') {
    return BadgeStatusType.Info;
  }

  // "INNE" oznacza inny status - pokazujemy Neutral (szary, tekst "Inne")
  if (lowerStatus === 'inne') {
    return BadgeStatusType.Neutral;
  }

  // "NIE DOTYCZY" - również Info
  if (lowerStatus.includes('nie dotyczy')) {
    return BadgeStatusType.Info;
  }

  // Statusy negatywne - MUSZĄ być sprawdzane PRZED pozytywnymi
  // bo np. "niezgodne" zawiera "zgodne", "niesprawny" zawiera "sprawny"
  if (
    lowerStatus === 'nie' ||
    lowerStatus.includes('niesprawny') ||
    lowerStatus.includes('nieprawidłowy') ||
    lowerStatus.includes('nie działa') ||
    lowerStatus.includes('uszkodzony') ||
    lowerStatus.includes('niezgodne') ||
    lowerStatus.includes('występują') ||
    lowerStatus.includes('do wymiany')
  ) {
    return BadgeStatusType.Error;
  }

  // Statusy pozytywne
  if (
    lowerStatus.includes('tak') ||
    lowerStatus.includes('prawidłowy') ||
    lowerStatus.includes('sprawny') ||
    lowerStatus.includes('działa') ||
    lowerStatus.includes('zgodne') ||
    lowerStatus.includes('ok') ||
    lowerStatus.includes('bez uwag')
  ) {
    return BadgeStatusType.Success;
  }

  // Ostrzeżenia
  if (
    lowerStatus.includes('wymaga uwagi') ||
    lowerStatus.includes('częściowo')
  ) {
    return BadgeStatusType.Warning;
  }

  return BadgeStatusType.Neutral;
};

/**
 * Maps visual assessment text to rating (1-4)
 * Returns null if assessment is not provided
 */
export const mapVisualAssessmentToRating = (
  assessment?: string,
): 1 | 2 | 3 | 4 | null => {
  if (!assessment) return null;

  const lowerAssessment = assessment.toLowerCase();
  if (
    lowerAssessment === 'bardzo dobry' ||
    lowerAssessment === 'bardzo dobra' ||
    lowerAssessment === 'dobry' ||
    lowerAssessment === 'dobra' ||
    lowerAssessment === 'idealny' ||
    lowerAssessment === 'idealna'
  ) {
    return 4;
  }
  if (
    lowerAssessment === 'bez uwag' ||
    lowerAssessment === 'zadowalający' ||
    lowerAssessment === 'adekwatna do przebiegu'
  ) {
    return 3;
  }
  if (
    lowerAssessment === 'średni' ||
    lowerAssessment === 'średnia' ||
    lowerAssessment === 'zły' ||
    lowerAssessment === 'zła'
  ) {
    return 2;
  }
  if (lowerAssessment === 'bardzo zły' || lowerAssessment === 'bardzo zła') {
    return 1;
  }

  return null;
};

/**
 * Maps coolant color text from API to OilColor enum
 * Returns null if color is not provided or cannot be mapped
 */
export const mapCoolantColorToOilColor = (
  colorText?: string,
): OilColor | null => {
  if (!colorText) return null;

  const lowerColor = colorText.toLowerCase().trim();

  // Mapowanie polskich i angielskich nazw kolorów
  if (lowerColor.includes('zielon') || lowerColor.includes('green')) {
    return OilColor.GREEN;
  }
  if (lowerColor.includes('niebieski') || lowerColor.includes('blue')) {
    return OilColor.BLUE;
  }
  if (
    lowerColor.includes('czerwon') ||
    lowerColor.includes('red') ||
    lowerColor.includes('bordowy')
  ) {
    return OilColor.RED;
  }
  if (lowerColor.includes('różow') || lowerColor.includes('pink')) {
    return OilColor.PINK;
  }
  if (lowerColor.includes('pomarańcz') || lowerColor.includes('orange')) {
    return OilColor.ORANGE;
  }
  if (lowerColor.includes('żółt') || lowerColor.includes('yellow')) {
    return OilColor.YELLOW;
  }
  if (lowerColor.includes('mlecz') || lowerColor.includes('milky')) {
    return OilColor.MILKY;
  }
  if (lowerColor.includes('złot') || lowerColor.includes('gold')) {
    return OilColor.GOLD;
  }
  if (lowerColor.includes('biał') || lowerColor.includes('white')) {
    return OilColor.WHITE;
  }
  if (lowerColor.includes('brązow') || lowerColor.includes('brown')) {
    return OilColor.BROWN;
  }
  if (lowerColor.includes('czarn') || lowerColor.includes('black')) {
    return OilColor.BLACK;
  }

  return null;
};
